/**
 * The feed (web-plan.md F1/D48): the 300-message window, groups, the two
 * dividers, typing, the jump pill — and the scroll policy ported whole:
 * `pinned` within 48 px of the bottom, own messages force the pin, sticking
 * to the bottom settles for 700 ms of resizes, prepends keep their anchor by
 * DOM id, and `overflow-anchor: none` means the feed keeps its OWN anchor.
 *
 * No JS virtualization on purpose (D48): the window is capped at 300 and a
 * virtualizer fights the anchoring this file exists to protect.
 */
import {
  For,
  type JSX,
  Show,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";

import type { Dict } from "../lib/i18n";
import * as mut from "../store/mutators";
import {
  BOTTOM_SLACK,
  GLIDE_MS,
  PENDING_SEQ,
  SEEN_SETTLE_MS,
  SETTLE_MS,
  TOP_TRIGGER,
  type Bubble,
  type FeedState,
} from "../store/types";
import { AvatarCircle } from "./avatar";
import { BubbleView, ServiceLine, type BubbleActions } from "./bubble";
import { ActivityIndicator, Icon } from "./ds";

interface Group {
  key: string;
  senderId: number;
  senderName: string;
  own: boolean;
  createdAt: number;
  avatarFileId: string | null;
  bubbles: Bubble[];
  dateLabel: string | null;
  unreadAbove: boolean;
}

export function FeedView(props: {
  feed: FeedState;
  t: Dict;
  isDm: boolean;
  selfId: number;
  personOf: (id: number) => { createdAt: number; avatarFileId: string | null };
  nameOf: (id: number) => string;
  typingText: string;
  actions: BubbleActions & {
    loadOlder(): void;
    markRead(): void;
    markReadUpTo(seen: number): void;
    openCard(userId: number): void;
  };
}): JSX.Element {
  let scroller!: HTMLDivElement;
  const [pinned, setPinned] = createSignal(true);

  const groups = createMemo<Group[]>(() => buildGroups(props));

  // The newest row's identity, BOTH halves: an optimistic bubble has no seq,
  // a server one no nonce, and the moment they reconcile the row is worth
  // following down again (its height changes when the tick appears).
  const newestKey = createMemo(() => {
    const last = props.feed.bubbles.at(-1);
    return last ? `${last.message.nonce}-${last.message.seq}` : "";
  });

  // Until when the feed is scrolling itself. A smooth arrival passes through
  // every position between here and the end, and `onScroll` would read the
  // first of them as "the reader scrolled up": the pin would drop mid-flight
  // and the jump pill blink over a feed already on its way down. So while our
  // own scroll runs the pin may only be set, never cleared.
  let stickUntil = 0;
  // The arrival's own scroll, while one is in the air.
  let flight = 0;
  const stopGlide = () => {
    if (flight) cancelAnimationFrame(flight);
    flight = 0;
  };

  // Whether the reader has taken the feed back from the follow.
  //
  // The follow runs for 700 ms after every arrival, and an opening channel is a
  // run of arrivals — so a reader who switches to a chat and starts scrolling
  // up straight away is scrolling against a follow that is still running: every
  // picture that finishes decoding resizes the column and pulls them back down,
  // and the last thing the window does is land them at the very bottom. Wait a
  // moment first and none of it happens, which is the shape the bug is felt in.
  //
  // Position cannot tell that scroll from ours — that is why `stickUntil`
  // exists at all. The gesture can: a wheel is the reader's hand and nothing
  // the follow does raises one, and a touch drag downwards is the same gesture
  // on a phone. Only while the follow is actually holding them at the end,
  // though: over a feed already away from its end there is nothing to hand
  // over, and over one too short to scroll a gesture that arrived before the
  // channel loaded would otherwise cancel the opening scroll and leave the
  // reader at the top of the history.
  //
  // It comes down again when the reader returns to the bottom themselves,
  // within the same slack the pin is decided by, so the two can never disagree
  // about which end of the feed the reader is at — and only then: see
  // `onScroll`.
  let free = false;
  const gapNow = () => scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
  const takeOver = () => {
    if (free || scroller.scrollHeight - scroller.clientHeight <= BOTTOM_SLACK) return;
    // Away from the end AND standing still. A flight is holding the reader
    // whether it has reached the bottom yet or not, and a tall arrival spends
    // most of its flight further off than the slack — so while one is in the
    // air the distance stops being the question and the gesture answers it
    // alone. Left to the distance, a wheel raised against a picture on its way
    // in reads as a wheel over a feed nobody is pulling, and the flight goes
    // on pulling.
    if (gapNow() > BOTTOM_SLACK && !flight) return;
    free = true;
    stopGlide();
    // Every scroll still queued by the follow asks `toBottom`, which now
    // stands down; the settle window has to stop holding a pin the reader has
    // just contradicted, or the jump pill cannot come back.
    stickUntil = 0;
  };

  // Written by hand rather than handed to `scrollTo({behavior:"smooth"})`, for
  // three reasons the browser's own animation cannot give:
  //
  //  * It cannot be cut. Any `scrollTop` written while a native smooth scroll
  //    runs ends it on the spot, and this feed writes them — the anchor below
  //    holds the reading position on every mutation, and an arrival is a burst
  //    of them (the tick landing, the stamp appearing). Each frame here is
  //    computed from where the flight began and how far through it is, so a
  //    nudge in between is overwritten on the next one and the movement carries
  //    on to the end.
  //  * It is one tempo. Chromium scales a native smooth scroll by distance,
  //    which for a one-line message is under a tenth of a second: a jump with
  //    extra steps. This takes GLIDE_MS whatever the distance.
  //  * It only needs frames, where a native scroll animation asks the
  //    compositor and quietly does nothing when the page is not being
  //    composited — leaving the instant correction at the end of the settle
  //    window to teleport the column.
  //
  // The target is read on every frame rather than captured, so a picture
  // decoding mid-flight is absorbed by the movement already running instead of
  // starting a second one behind it; and a `glide` called again while one is in
  // the air joins it rather than restarting its clock, which is what keeps a
  // resize per frame from stretching the movement forever. The easing is
  // `--ease-emphasized`'s shape without its solver: a cubic ease-out.
  const glide = () => {
    if (flight) return;
    const from = scroller.scrollTop;
    const t0 = performance.now();
    const step = () => {
      if (!flight) return;
      if (free) {
        flight = 0;
        return;
      }
      const to = scroller.scrollHeight - scroller.clientHeight;
      const p = Math.min(1, (performance.now() - t0) / GLIDE_MS);
      scroller.scrollTop = from + (to - from) * (1 - (1 - p) ** 3);
      if (p < 1) flight = requestAnimationFrame(step);
      else {
        scroller.scrollTop = to;
        flight = 0;
      }
    };
    flight = requestAnimationFrame(step);
  };

  // The short move glides — a message arriving at the end rises into place
  // instead of teleporting the column. Only the short one: a gap taller than
  // the window is an opening feed, and animating that is a blur, not a
  // transition. The last move, after the feed has settled, is the exact
  // landing — and it stands down while a flight is still in the air, which
  // ends at that same place a moment later anyway.
  const toBottom = (soft: boolean) => {
    if (free) return;
    const gap = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    if (gap <= 0) return;
    const calm = matchMedia("(prefers-reduced-motion:reduce)").matches;
    if (soft && !calm && gap <= scroller.clientHeight) {
      glide();
    } else {
      stopGlide();
      scroller.scrollTop = scroller.scrollHeight;
    }
  };

  const stickToBottom = () => {
    if (free) return;
    // Not a single scroll: albums decode late, and one scroll landing short
    // reads as "the reader scrolled up" and drops the pin.
    stickUntil = Date.now() + SETTLE_MS;
    toBottom(true);
    requestAnimationFrame(() => {
      toBottom(true);
    });
    const observer = new ResizeObserver(() => {
      toBottom(true);
    });
    observer.observe(scroller);
    if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);
    setTimeout(() => {
      observer.disconnect();
      if (!flight) toBottom(false);
    }, SETTLE_MS);
  };

  createEffect((prev: string | undefined) => {
    const key = newestKey();
    if (prev === key) return key;
    const last = props.feed.bubbles.at(-1);
    const own = last?.isOwn && last.state !== "sent";
    // Arrival policy: follow only when already pinned — except an own
    // message, which forces the pin (nobody writes in order to look away) and
    // with it takes the feed back from a reader who had scrolled away.
    if (own) {
      setPinned(true);
      free = false;
    }
    if (pinned() || own) {
      queueMicrotask(stickToBottom);
      if (document.visibilityState === "visible") props.actions.markRead();
    }
    return key;
  });

  // Prepends keep their anchor by DOM id — a node can be rebuilt when a page
  // merges into the first group, but `msg-<seq>` survives.
  let anchorId: string | null = null;
  let anchorTop = 0;
  const recordAnchor = () => {
    if (scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= BOTTOM_SLACK) {
      anchorId = null;
      return;
    }
    const rows = scroller.querySelectorAll<HTMLElement>('[id^="msg-"]');
    const top = scroller.getBoundingClientRect().top;
    for (const row of rows) {
      const r = row.getBoundingClientRect();
      if (r.bottom > top) {
        anchorId = row.id;
        anchorTop = r.top - top;
        return;
      }
    }
    anchorId = null;
  };
  createEffect(() => {
    void props.feed.bubbles.length;
    if (anchorId) {
      const row = document.getElementById(anchorId);
      if (row) {
        const delta = row.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
        scroller.scrollTop += delta - anchorTop;
      }
      anchorId = null;
    }
  });

  // Reading is not only the bottom (D249). A reader half a screen above the
  // end is still reading, and a badge counting the messages they have already
  // passed is the client telling them they have not — which it did until the
  // channel was left and opened again. So the newest row that has WHOLLY
  // passed the fold is reported as read, and the runtime clamps it to a
  // position the feed actually holds before it can reach `POST /read`.
  //
  // Whole and not merely touched: a sliver of the next bubble under the last
  // line of this one is not a message anybody has read — which is also why
  // this is a search and not an `IntersectionObserver`, whose default
  // threshold is exactly that sliver.
  //
  // The search is the anchor's bisection upside down: same column, and the
  // same reason for bisecting one — three hundred rows in document order,
  // whose edges only ever increase.
  let seenMark = 0;
  let seenTimer: number | undefined;
  const scanSeen = () => {
    seenTimer = undefined;
    if (document.visibilityState !== "visible" || !document.hasFocus()) return;
    const rows = scroller.querySelectorAll<HTMLElement>('[id^="msg-"]');
    const fold = scroller.getBoundingClientRect().bottom;
    let lo = 0;
    let hi = rows.length - 1;
    let at: HTMLElement | null = null;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (rows[mid]!.getBoundingClientRect().bottom <= fold) {
        at = rows[mid]!;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    if (at === null) return;
    const seq = Number(at.id.slice("msg-".length));
    // The high-water mark is this side's, so a position skipped while the tab
    // was in the background would never be offered again — which is why
    // `focus` and `visibilitychange` re-run the search rather than only the
    // scroll: coming back to a feed that already shows the answer is enough.
    if (seq > seenMark) {
      seenMark = seq;
      props.actions.markReadUpTo(seq);
    }
  };
  // After the position holds still, not on each event: what the search finds
  // becomes a request, and a flick through a hundred messages should cost a
  // handful of read marks and not a hundred.
  const scanSeenSoon = () => {
    clearTimeout(seenTimer);
    seenTimer = setTimeout(scanSeen, SEEN_SETTLE_MS);
  };

  const onScroll = () => {
    const gap = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    const atBottom = gap <= BOTTOM_SLACK;
    // The reader is at the end again, by their own hand: the follow is the
    // feed's once more. By their own hand is the whole rule — a flight writes
    // a scroll position on every frame of its own, and those frames end within
    // the slack, so without asking about one here a wheel raised in the middle
    // of a flight would be put straight back down by the very movement it was
    // meant to stop.
    if (atBottom && !flight) free = false;
    if (atBottom || Date.now() >= stickUntil) setPinned(atBottom);
    if (atBottom && document.visibilityState === "visible") props.actions.markRead();
    scanSeenSoon();
    if (scroller.scrollTop < TOP_TRIGGER && !props.feed.loadingOlder && !props.feed.reachedStart) {
      recordAnchor();
      props.actions.loadOlder();
    }
  };

  // A channel that fits the window scrolls exactly never: the rows on screen
  // when it opens, and every row a growing feed puts above the fold, are read
  // without a scroll event ever firing.
  createEffect(() => {
    void props.feed.bubbles.length;
    scanSeenSoon();
  });

  // A different channel is a different feed, and the hand-off does not travel
  // with the reader: this view is not remounted per channel the way the
  // desktop's `Feed` is keyed on one.
  createEffect((prev: number | undefined) => {
    const id = props.feed.channelId;
    if (prev !== undefined && prev !== id) {
      free = false;
      // Nor does a flight: the column this one is walking to the bottom is
      // about to be holding another channel's messages. (The desktop has
      // nothing to do here — its `Feed` is keyed on the channel, so the flight
      // is parked on a column that goes away with it.)
      stopGlide();
    }
    return id;
  });

  // The two gestures, listened for on the column itself and passively: a
  // non-passive wheel or touch listener on a scroller is exactly the thing
  // that makes scrolling on a phone feel late.
  const onWheel = (e: WheelEvent) => {
    if (e.deltaY < 0) takeOver();
  };
  let touchFrom: number | null = null;
  const touchY = (e: TouchEvent) => (e.touches[0] ? e.touches[0].clientY : null);
  const onTouchStart = (e: TouchEvent) => {
    touchFrom = touchY(e);
  };
  const onTouchMove = (e: TouchEvent) => {
    const now = touchY(e);
    if (touchFrom !== null && now !== null && now > touchFrom) takeOver();
  };
  onMount(() => {
    scroller.addEventListener("wheel", onWheel, { passive: true });
    scroller.addEventListener("touchstart", onTouchStart, { passive: true });
    scroller.addEventListener("touchmove", onTouchMove, { passive: true });
  });
  onCleanup(() => {
    scroller.removeEventListener("wheel", onWheel);
    scroller.removeEventListener("touchstart", onTouchStart);
    scroller.removeEventListener("touchmove", onTouchMove);
  });

  const onVisibility = () => {
    if (document.visibilityState === "visible" && pinned()) props.actions.markRead();
    scanSeenSoon();
  };
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("focus", scanSeenSoon);
  onCleanup(() => {
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("focus", scanSeenSoon);
    clearTimeout(seenTimer);
  });

  const newBelow = createMemo(() => mut.newBelow(props.feed));

  return (
    <div class="chat-feed">
      <div class="chat-feed__scroll" id="feed-scroll" ref={scroller} onScroll={onScroll}>
        <div class="chat-feed__inner">
          <Show when={props.feed.loadingOlder}>
            <div class="chat-feed__top">
              <ActivityIndicator small />
            </div>
          </Show>
          <For each={groups()}>
            {(group) => (
              <>
                <Show when={group.dateLabel}>
                  <div class="feed-divider feed-divider--date">
                    <span>{group.dateLabel}</span>
                  </div>
                </Show>
                <Show when={group.unreadAbove}>
                  <div class="feed-divider">
                    <span>{props.t.unreadSeparator()}</span>
                  </div>
                </Show>
                <GroupView group={group} {...props} />
              </>
            )}
          </For>
          <Show when={props.typingText !== ""}>
            <div class="typing-indicator">
              <span>{props.typingText}</span>
              <span class="typing-indicator__dots">
                <i />
                <i />
                <i />
              </span>
            </div>
          </Show>
        </div>
      </div>
      <Show when={!pinned()}>
        <button
          type="button"
          class="jump-to-latest"
          onClick={() => {
            setPinned(true);
            // The button is only here because the reader left the bottom, so
            // the follow is theirs — and clicking it is asking for the end.
            free = false;
            stickToBottom();
            props.actions.markRead();
          }}
        >
          <span class="jump-to-latest__icon">
            <Icon name="arrow_downward" size={18} />
          </span>
          <Show when={newBelow() > 0}>
            <span class="jump-to-latest__badge">{newBelow() > 99 ? "99+" : newBelow()}</span>
          </Show>
        </button>
      </Show>
    </div>
  );
}

function GroupView(props: {
  group: Group;
  feed: FeedState;
  t: Dict;
  isDm: boolean;
  personOf: (id: number) => { createdAt: number; avatarFileId: string | null };
  nameOf: (id: number) => string;
  actions: BubbleActions & { openCard(userId: number): void };
}): JSX.Element {
  const g = () => props.group;
  const service = () => g().bubbles[0]?.message.service != null;
  // A DM has exactly one possible other sender, so their face beside every
  // group is a repeat of what the chat header already says — drop it and
  // let the bubble reclaim the width it stood in (see .message-group--no-face).
  const dmPeer = () => !g().own && props.isDm;
  return (
    <Show
      when={!service()}
      fallback={
        <ServiceLine bubble={g().bubbles[0]!} t={props.t} objectName={props.nameOf} />
      }
    >
      <div
        class={`message-group${g().own ? " message-group--own" : ""}${dmPeer() ? " message-group--no-face" : ""}`}
      >
        <Show when={!g().own && !props.isDm}>
          <span
            class="message-group__face"
            role="button"
            tabindex="0"
            onClick={() => props.actions.openCard(g().senderId)}
          >
            <AvatarCircle
              name={g().senderName}
              createdAt={g().createdAt}
              size={40}
              fileId={g().avatarFileId}
            />
          </span>
        </Show>
        <div class="message-group__column">
          <Show when={!g().own}>
            <span
              class="message-group__author message-group__author--link"
              role="button"
              onClick={() => props.actions.openCard(g().senderId)}
            >
              {g().senderName}
            </span>
          </Show>
          <For each={g().bubbles}>
            {(bubble) => (
              <BubbleView
                bubble={bubble}
                t={props.t}
                actions={props.actions}
              />
            )}
          </For>
        </div>
      </div>
    </Show>
  );
}

function buildGroups(props: {
  feed: FeedState;
  t: Dict;
  personOf: (id: number) => { createdAt: number; avatarFileId: string | null };
}): Group[] {
  const out: Group[] = [];
  let current: Group | null = null;
  let prevDay: string | null = null;

  for (const b of props.feed.bubbles) {
    const day = dayLabel(b.message.sent_at_ms, props.t);
    const dayChanged = day !== prevDay;
    prevDay = day;
    const unreadAbove = props.feed.dividerBefore === b.message.seq;

    if (b.showHeader || dayChanged || unreadAbove || current === null) {
      const person = props.personOf(b.message.sender_id);
      current = {
        key: `${b.message.channel_id}-${b.message.seq}-${b.message.nonce}`,
        senderId: b.message.sender_id,
        senderName: b.senderName,
        own: b.isOwn,
        createdAt: person.createdAt,
        avatarFileId: person.avatarFileId,
        bubbles: [b],
        dateLabel: dayChanged ? day : null,
        unreadAbove,
      };
      out.push(current);
    } else {
      current.bubbles.push(b);
    }
  }
  return out;
}

function dayLabel(atMs: number, t: Dict): string {
  const at = new Date(atMs === 0 ? Date.now() : atMs);
  const now = new Date();
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(at, now)) return t.today();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(at, yesterday)) return t.yesterday();
  return at.toLocaleDateString(undefined, { day: "numeric", month: "long" });
}

export { PENDING_SEQ };
